/**
 * The metric register — the real one
 *
 * Replaces the static Semantic Layer mockup. Every row here comes from
 * /api/core/metrics; nothing is demo content.
 *
 * Two things this screen exists to make visible, which a dashboard normally
 * hides:
 *
 *   WHAT A NUMBER COUNTS. Each definition is rendered in English by the
 *   server, because certification is meaningless if the thing being certified
 *   is only readable as a formula.
 *
 *   WHEN THE SYSTEM WILL NOT ANSWER. An uncertified metric resolves to a
 *   refusal with the route to fixing it, and that is shown as the result it
 *   is rather than as an error. It is the most unusual behaviour in the
 *   product and the easiest to mistake for a bug, so it is given room.
 */

import React, { useCallback, useEffect, useState } from 'react';
import { AppShell } from '../../components/shell/AppShell';
import { Card, PageHead, Note, Tag, Table, Tone } from '../../components/ui';
import {
  CertificationState,
  MetricResult,
  MetricSummary,
  coreService,
  formatMetric,
  isRefusal,
} from '../services/core.service';

const STATE_TONE: Record<CertificationState, Tone> = {
  certified: 'g',
  proposed: 'a',
  draft: 'n',
  disputed: 'r',
  retired: 'd',
};

/** What the state means for whether a figure will be reported at all. */
const STATE_MEANING: Record<CertificationState, string> = {
  certified: 'Reported. The owner has agreed this is how it is counted.',
  proposed: 'Not reported until the owner certifies it.',
  draft: 'Not reported. Still being written.',
  disputed: 'Not reported. Someone has said the number is wrong.',
  retired: 'No longer in use.',
};

export const MetricRegisterPage: React.FC = () => {
  const [metrics, setMetrics] = useState<MetricSummary[]>([]);
  const [selected, setSelected] = useState<string | null>(null);
  const [result, setResult] = useState<MetricResult | null>(null);
  const [loading, setLoading] = useState(true);
  const [resolving, setResolving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    coreService
      .listMetrics()
      .then((rows) => {
        if (cancelled) return;
        setMetrics(rows);
        setLoading(false);
      })
      .catch((e: unknown) => {
        if (cancelled) return;
        setError(e instanceof Error ? e.message : 'Could not load the metric register.');
        setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, []);

  const resolve = useCallback(async (key: string) => {
    setSelected(key);
    setResolving(true);
    setResult(null);
    try {
      setResult(await coreService.resolveMetric(key));
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not resolve that metric.');
    } finally {
      setResolving(false);
    }
  }, []);

  const certified = metrics.filter((m) => m.certificationState === 'certified').length;

  return (
    <AppShell
      title="Metric register"
      sub={`${metrics.length} definitions · ${certified} certified`}
    >
      <PageHead
        eyebrow="Semantic layer"
        title="What every number means, and who agreed to it"
        lede="A metric is not a query someone wrote once. It is a definition with a formula, a grain, an authoritative source and a named owner. Until that owner certifies it, OmniMind will not report a figure for it."
      />

      {error && (
        <Note style={{ marginBottom: 12 }}>
          <strong>Could not reach the register.</strong> {error}
        </Note>
      )}

      {loading && <Card><p>Loading the register…</p></Card>}

      {!loading && metrics.length === 0 && !error && (
        <Card title="Nothing defined yet">
          <p>
            No metrics have been installed for this organization. Run the Finance
            starter catalog to add nine definitions as proposals — none of them will
            report a figure until someone certifies it.
          </p>
        </Card>
      )}

      {!loading && metrics.length > 0 && (
        <Card
          title="Definitions"
          sub="Select one to resolve it against the connected source"
          flush
        >
          <Table
            cols={[
              { t: 'Metric' },
              { t: 'What it counts' },
              { t: 'Owner' },
              { t: 'State' },
            ]}
            rows={metrics.map((m) => [
              <button
                key={`${m.key}-name`}
                className="btn btn-g"
                style={{ textAlign: 'left' }}
                onClick={() => resolve(m.key)}
              >
                {m.label}
              </button>,
              <span key={`${m.key}-exp`} style={{ fontSize: 12, lineHeight: 1.45 }}>
                {m.explanation}
              </span>,
              m.owner ?? <em key={`${m.key}-own`}>nobody yet</em>,
              <Tag key={`${m.key}-st`} tone={STATE_TONE[m.certificationState]} dot>
                {m.certificationState}
              </Tag>,
            ])}
          />
        </Card>
      )}

      {selected && (
        <Card
          title={`Resolving ${selected}`}
          sub={resolving ? 'Asking the source…' : undefined}
          className="mt"
        >
          {resolving && <p>Resolving against the connected source…</p>}

          {!resolving && result && isRefusal(result) && (
            <>
              {/* A refusal is the answer, not an error. */}
              <Tag tone="a" dot>
                {result.reason.replace(/_/g, ' ')}
              </Tag>
              <p style={{ marginTop: 10, fontWeight: 600 }}>{result.message}</p>
              <Note>
                <strong>To get a number:</strong> {result.remedy}
              </Note>
              <p style={{ fontSize: 12, opacity: 0.75, marginTop: 10 }}>
                This is deliberate. A refusal costs a conversation; a wrong number
                shown to an executive costs the account.
              </p>
            </>
          )}

          {!resolving && result && !isRefusal(result) && (
            <>
              <div style={{ fontSize: 32, fontWeight: 700, letterSpacing: '-0.02em' }}>
                {formatMetric(result.value, result.unit, result.currency)}
              </div>
              <p style={{ fontSize: 12, opacity: 0.8, marginTop: 4 }}>
                as at {result.referenceDate} · {result.rowsConsidered.toLocaleString()}{' '}
                source records · {result.computeMs}ms · certified by{' '}
                {result.owner ?? 'nobody'} · source {result.authoritativeSource}
              </p>

              {result.warnings.map((w, i) => (
                <Note key={i} style={{ marginTop: 10 }}>
                  {w}
                </Note>
              ))}

              {result.conflicts.map((c) => (
                <Note key={c.sourceId} style={{ marginTop: 10 }}>
                  <strong>{c.label} reports {c.value.toLocaleString()}</strong> —{' '}
                  {c.differencePercent}% apart.{' '}
                  {c.windowMisaligned
                    ? 'The two cover different periods, so this is not a like-for-like comparison.'
                    : c.note}
                  {c.knownDifferences.length > 0 && (
                    <ul style={{ marginTop: 6, paddingLeft: 18 }}>
                      {c.knownDifferences.map((d, i) => (
                        <li key={i} style={{ fontSize: 12 }}>
                          {d}
                        </li>
                      ))}
                    </ul>
                  )}
                </Note>
              ))}

              <div className="sec-t" style={{ marginTop: 16 }}>
                How this number was built
              </div>
              <ol style={{ paddingLeft: 18, fontSize: 13 }}>
                {result.lineage.map((step, i) => (
                  <li key={i} style={{ marginBottom: 4 }}>
                    {step.description}
                    {step.rowCount > 0 && (
                      <span style={{ opacity: 0.7 }}>
                        {' '}
                        ({step.rowCount.toLocaleString()} rows)
                      </span>
                    )}
                  </li>
                ))}
              </ol>

              {(() => {
                const samples = result.lineage.flatMap((s) => s.sampleRows).slice(0, 5);
                if (samples.length === 0) return null;
                return (
                  <>
                    <div className="sec-t" style={{ marginTop: 14 }}>
                      Documents you can open in the source system
                    </div>
                    <Table
                      cols={[{ t: 'Reference' }, { t: 'Date' }, { t: 'Contribution', num: true }]}
                      rows={samples.map((r) => [
                        r.reference ?? String(r.sourceId),
                        r.documentDate ?? '—',
                        r.contribution === null
                          ? '—'
                          : (r.contribution / 100).toLocaleString(undefined, {
                              minimumFractionDigits: 2,
                              maximumFractionDigits: 2,
                            }),
                      ])}
                      maxHeight={220}
                    />
                  </>
                );
              })()}
            </>
          )}
        </Card>
      )}

      {metrics.length > 0 && (
        <Note style={{ marginTop: 14 }}>
          <strong>Why a metric might refuse.</strong>{' '}
          {Object.entries(STATE_MEANING)
            .filter(([state]) => metrics.some((m) => m.certificationState === state))
            .map(([state, meaning]) => `${state}: ${meaning}`)
            .join(' ')}
        </Note>
      )}
    </AppShell>
  );
};
